import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  MessageCircle,
  Mail,
  Home,
} from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_janmobile-checkout/artifacts/44154y5j_JanMobileLogo.png";
const WHATSAPP = "+32488838808";
const SUPPORT_EMAIL = "sales@janmobile.be";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL = 2000;

const Row = ({ label, value, highlight }) => (
  <div className="flex items-center justify-between py-2 border-b border-orange-100 last:border-b-0">
    <span className="text-[11px] uppercase tracking-wider text-orange-900/60">
      {label}
    </span>
    <span
      className={`text-sm font-semibold ${
        highlight ? "text-green-700" : "text-orange-950"
      }`}
    >
      {value}
    </span>
  </div>
);

export default function SuccessPage() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "loading", data: null, error: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", data: null, error: "No session" });
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        const d = res.data;
        if (d.payment_status === "paid") {
          if (!cancelled) setState({ status: "paid", data: d, error: null });
          return;
        }
        if (d.status === "expired") {
          if (!cancelled) setState({ status: "expired", data: d, error: null });
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          if (!cancelled) setState({ status: "timeout", data: d, error: null });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (!cancelled)
          setState({
            status: "error",
            data: null,
            error: e?.response?.data?.detail || e.message,
          });
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const renderBody = () => {
    if (state.status === "loading") {
      return (
        <div className="text-center py-8" data-testid="payment-loading">
          <Loader2 size={42} className="mx-auto animate-spin text-orange-600" />
          <h2 className="mt-4 text-xl font-bold text-orange-950">
            {t("paymentLoading")}
          </h2>
          <p className="text-orange-900/70 mt-1.5 text-sm">
            {t("paymentLoadingHint")}
          </p>
        </div>
      );
    }

    if (state.status === "paid") {
      const d = state.data;
      return (
        <div className="text-center py-8" data-testid="payment-success">
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto">
            <CheckCircle2 size={32} className="text-green-600" />
          </div>
          <h2 className="mt-4 text-2xl font-extrabold text-orange-950">
            {t("paymentSuccess")}
          </h2>
          <p className="text-orange-900/70 mt-1.5 text-sm">
            {t("paymentSuccessHint")}
          </p>
          <div className="mt-5 bg-orange-50 border border-orange-100 rounded-2xl p-4 text-left max-w-md mx-auto">
            <Row label={t("bundle")} value={d.package_name} />
            <Row label={t("mobileNr")} value={d.mobile_number} />
            <Row label={t("emailLabel")} value={d.email} />
            <Row
              label={t("amount")}
              value={`\u20AC ${Number(d.amount_total).toFixed(2).replace(".", ",")}`}
            />
            <Row label={t("status")} value={t("paid")} highlight />
          </div>
          <p className="text-[11px] text-orange-900/60 mt-3">
            {t("confirmationSentTo")} {d.email}.
          </p>
        </div>
      );
    }

    let errorTitleKey = "somethingWrong";
    if (state.status === "expired") {
      errorTitleKey = "sessionExpired";
    } else if (state.status === "timeout") {
      errorTitleKey = "timeout";
    }

    return (
      <div className="text-center py-8" data-testid="payment-failed">
        <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto">
          <XCircle size={32} className="text-red-600" />
        </div>
        <h2 className="mt-4 text-2xl font-extrabold text-orange-950">
          {t(errorTitleKey)}
        </h2>
        {state.error && (
          <p className="text-orange-900/70 mt-1.5 text-sm">{state.error}</p>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-black text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2.5 sm:gap-3">
            <img src={LOGO_URL} alt="Jan Mobile - Prepaid SIM" className="h-12 sm:h-14 w-auto rounded-full" />
            <span className="font-extrabold tracking-tight text-lg sm:text-2xl leading-none">
              <span className="text-white">JanMobiel</span>
              <span className="text-orange-500">TopUp</span>
            </span>
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto px-4 py-6 w-full">
        <div className="bg-white rounded-2xl border border-orange-100">
          {renderBody()}
          <div className="border-t border-orange-100 p-4 flex flex-wrap gap-2 items-center justify-center">
            <Link
              to="/"
              data-testid="back-to-shop"
              className="koop-button rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5"
            >
              <Home size={14} /> {t("backToShop")}
            </Link>
            <a
              href={`https://wa.me/${WHATSAPP.replace(/\+/g, "")}`}
              target="_blank"
              rel="noreferrer"
              className="rounded-full px-4 py-2 text-sm font-semibold border border-orange-200 text-orange-700 hover:bg-orange-50 inline-flex items-center gap-1.5"
            >
              <MessageCircle size={14} /> {t("whatsapp")}
            </a>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="rounded-full px-4 py-2 text-sm font-semibold border border-orange-200 text-orange-700 hover:bg-orange-50 inline-flex items-center gap-1.5"
            >
              <Mail size={14} /> {t("email")}
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
